// src/lib/logger.js
// Secure logging utility with environment-based controls
// In production: only errors are logged, and sensitive data is sanitized
// In development: all logs are shown for debugging
// Integrates with Sentry for remote error tracking

import { captureException, captureMessage } from '@/lib/sentry';

const isDev = import.meta.env.DEV;

// Patterns to detect and sanitize sensitive data
const SENSITIVE_PATTERNS = [
  // Email addresses
  /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
  // UUIDs (user IDs, entity IDs)
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
  // JWT tokens
  /eyJ[a-zA-Z0-9_-]*\.eyJ[a-zA-Z0-9_-]*\.[a-zA-Z0-9_-]*/g,
  // Bearer tokens
  /Bearer\s+[a-zA-Z0-9._-]+/gi,
  // API keys (common patterns)
  /sk_[a-zA-Z0-9]{20,}/g,
  /pk_[a-zA-Z0-9]{20,}/g,
  // Supabase anon/service keys
  /sbp_[a-zA-Z0-9]{20,}/g,
];

/**
 * Sanitize a string by replacing sensitive data with placeholders
 */
const sanitizeString = (str) => {
  if (typeof str !== 'string') return str;

  let sanitized = str;
  SENSITIVE_PATTERNS.forEach((pattern, index) => {
    const replacements = ['[EMAIL]', '[ID]', '[TOKEN]', '[TOKEN]', '[KEY]', '[KEY]', '[KEY]'];
    sanitized = sanitized.replace(pattern, replacements[index] || '[REDACTED]');
  });
  return sanitized;
};

/**
 * Deep sanitize an object or value for logging
 */
const sanitize = (value) => {
  if (value === null || value === undefined) return value;

  if (typeof value === 'string') {
    return sanitizeString(value);
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: sanitizeString(value.message),
      // Don't include stack in production
      ...(isDev && { stack: value.stack })
    };
  }

  if (Array.isArray(value)) {
    return value.map(sanitize);
  }

  if (typeof value === 'object') {
    const sanitized = {};
    for (const key of Object.keys(value)) {
      // Skip potentially sensitive keys entirely
      const lowerKey = key.toLowerCase();
      if (
        lowerKey.includes('password') ||
        lowerKey.includes('token') ||
        lowerKey.includes('secret') ||
        lowerKey.includes('key') ||
        lowerKey.includes('authorization') ||
        lowerKey.includes('credential')
      ) {
        sanitized[key] = '[REDACTED]';
      } else if (
        lowerKey === 'email' ||
        lowerKey.includes('email')
      ) {
        sanitized[key] = '[EMAIL]';
      } else {
        sanitized[key] = sanitize(value[key]);
      }
    }
    return sanitized;
  }

  return value;
};

/**
 * Format arguments for logging
 * In production: sanitize all data
 * In development: pass through as-is
 */
const formatArgs = (args) => {
  if (isDev) return args;
  return args.map(sanitize);
};

/**
 * Logger utility with environment-aware logging
 *
 * Usage:
 *   import { logger } from '@/lib/logger';
 *   logger.debug('Debug message', data);
 *   logger.info('Info message');
 *   logger.warn('Warning message');
 *   logger.error('Error occurred', error);
 */
export const logger = {
  /**
   * Debug level - only shown in development
   * Use for detailed debugging information
   */
  debug: (...args) => {
    if (isDev) {
      console.log(...args);
    }
    // Silent in production
  },

  /**
   * Info level - only shown in development
   * Use for general operational information
   */
  info: (...args) => {
    if (isDev) {
      console.log(...args);
    }
    // Silent in production
  },

  /**
   * Warning level - shown in development, sanitized in production
   * Use for recoverable issues that should be addressed
   */
  warn: (...args) => {
    if (isDev) {
      console.warn(...args);
    }
    // Silent in production for security
  },

  /**
   * Error level - always shown, sanitized in production
   * Use for actual errors that need attention
   * Also sends to Sentry for remote tracking
   */
  error: (...args) => {
    if (isDev) {
      console.error(...args);
    } else {
      // In production, sanitize but still log errors
      console.error(...formatArgs(args));
    }

    // Send to Sentry (handles its own sanitization)
    try {
      const firstArg = args[0];
      const context = args.length > 1 ? { details: args.slice(1) } : {};

      if (firstArg instanceof Error) {
        captureException(firstArg, context);
      } else if (typeof firstArg === 'string') {
        // If it's a string with an error object as second arg
        if (args[1] instanceof Error) {
          captureException(args[1], { message: firstArg, ...context });
        } else {
          captureMessage(firstArg, 'error', context);
        }
      }
    } catch (sentryError) {
      // Fail silently - don't break logging if Sentry fails
    }
  },

  /**
   * Group logs (dev only)
   */
  group: (label) => {
    if (isDev) {
      console.group(label);
    }
  },

  groupEnd: () => {
    if (isDev) {
      console.groupEnd();
    }
  },

  /**
   * Table output (dev only)
   */
  table: (data) => {
    if (isDev) {
      console.table(data);
    }
  },
};

// Also export individual functions for convenience
export const { debug, info, warn, error } = logger;

// Export isDev for other modules that need to check environment
export { isDev };
